import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Zap, Rocket, Diamond, CheckCircle2, Power, Volume2, VolumeX, Send, Lock } from 'lucide-react';
import api from '../api/axios';
import { getSpeedOptions, isSpeedAllowed } from '../utils/planRestrictions';

const CheckerSpeedModal = ({ isOpen, onClose, selectedSpeed, setSelectedSpeed, userPlan, userStatus, proxyEnabled, setProxyEnabled, soundEnabled, setSoundEnabled }) => {
  const [showGuide, setShowGuide] = React.useState(false);
  const [hitSender, setHitSender] = React.useState(null); // Start as null, fetch from API
  const [tempSpeed, setTempSpeed] = React.useState(selectedSpeed);
  const [tempProxy, setTempProxy] = React.useState(proxyEnabled);
  const [tempSound, setTempSound] = React.useState(soundEnabled);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Reset temp values when modal opens
    setTempSpeed(selectedSpeed);
    setTempProxy(proxyEnabled);
    setTempSound(soundEnabled);

    // Fetch user's hit sender preference - prioritize localStorage
    const fetchHitSender = async () => {
      setLoading(true);
      try {
        // First check localStorage for instant feedback
        const savedHitSender = localStorage.getItem('hitSender');
        if (savedHitSender && ['both', 'approved', 'live', 'off'].includes(savedHitSender)) {
          setHitSender(savedHitSender);
          console.log(`✅ Loaded hit_sender from localStorage: ${savedHitSender}`);
          setLoading(false);
          return;
        }
        
        // If not in localStorage, fetch from database
        const response = await api.get('/user/profile');
        if (response.data.success) {
          const fetchedHitSender = response.data.user.hit_sender || 'both';
          setHitSender(fetchedHitSender);
          localStorage.setItem('hitSender', fetchedHitSender); // Store for next time
          console.log(`✅ Fetched hit_sender from DB: ${fetchedHitSender}`);
        }
      } catch (error) {
        console.error('Failed to fetch hit sender preference');
        setHitSender('both'); // Fallback to both on error
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) {
      fetchHitSender();
    }
  }, [isOpen, selectedSpeed, proxyEnabled, soundEnabled]);

  const updateHitSender = async (value) => {
    setHitSender(value);
    localStorage.setItem('hitSender', value); // Save to localStorage immediately
    try {
      const response = await api.post('/user/update-hit-sender', { hit_sender: value });
      if (response.data.success) {
        console.log(`✅ Hit sender updated to: ${value}`);
      }
    } catch (error) {
      console.error('Failed to update hit sender:', error);
    }
  };

  // Determine speed access based on plan and status using utility
  const canAccessSpeed = (speedId) => {
    return isSpeedAllowed(userPlan, userStatus, speedId);
  };

  const speeds = [
    {
      id: 'slow',
      name: 'Slow',
      subtitle: 'Slowest but still kills',
      description: '1 concurrent request, 4s delay',
      icon: Clock,
      borderColor: 'border-orange-500',
      bgColor: 'bg-orange-900/20',
      textColor: 'text-orange-400',
      iconBg: 'bg-orange-900/40',
      locked: false, // Always available to all users
    },
    {
      id: 'normal',
      name: 'Normal',
      subtitle: canAccessSpeed('normal') ? 'Balanced speed and stability' : 'Premium plans required',
      description: canAccessSpeed('normal') ? '2 concurrent requests, 2s delay' : '◇ Requires Bronze or higher plan',
      icon: Zap,
      borderColor: canAccessSpeed('normal') ? 'border-orange-500' : 'border-gray-700',
      bgColor: canAccessSpeed('normal') ? 'bg-orange-900/20' : 'bg-gray-800/30',
      textColor: canAccessSpeed('normal') ? 'text-orange-400' : 'text-gray-500',
      iconBg: canAccessSpeed('normal') ? 'bg-orange-900/40' : 'bg-gray-800/40',
      locked: !canAccessSpeed('normal'),
    },
    {
      id: 'fast',
      name: 'Fast',
      subtitle: canAccessSpeed('fast') ? 'High speed checking' : 'Gold plan required',
      description: canAccessSpeed('fast') ? '3 concurrent requests, 1s delay' : '◇ Requires Gold plan',
      icon: Rocket,
      borderColor: canAccessSpeed('fast') ? 'border-orange-500' : 'border-gray-700',
      bgColor: canAccessSpeed('fast') ? 'bg-orange-900/20' : 'bg-gray-800/30',
      textColor: canAccessSpeed('fast') ? 'text-orange-400' : 'text-gray-500',
      iconBg: canAccessSpeed('fast') ? 'bg-orange-900/40' : 'bg-gray-800/40',
      locked: !canAccessSpeed('fast'),
    },
    {
      id: 'turbo',
      name: 'Turbo',
      subtitle: canAccessSpeed('turbo') ? 'Maximum speed' : 'Diamond plan required',
      description: canAccessSpeed('turbo') ? '5 concurrent requests, 0.5s delay' : '◇ Requires Diamond plan',
      icon: Diamond,
      borderColor: canAccessSpeed('turbo') ? 'border-orange-500' : 'border-gray-700',
      bgColor: canAccessSpeed('turbo') ? 'bg-orange-900/20' : 'bg-gray-800/30',
      textColor: canAccessSpeed('turbo') ? 'text-orange-400' : 'text-gray-500',
      iconBg: canAccessSpeed('turbo') ? 'bg-orange-900/40' : 'bg-gray-800/40',
      locked: !canAccessSpeed('turbo'),
    },
  ];

  const guideItems = [
    'Shopii uses client-side delays and concurrency control',
    'Start with Normal speed for best balance',
    'Use Slow speed during high server load',
    'Fast/Turbo modes for power users (Premium only)',
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full max-w-md bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700/50 shadow-2xl max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-b from-gray-900 to-transparent p-6 pb-4 border-b border-gray-800/50 backdrop-blur-xl z-10">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-800/50 rounded-lg transition-colors">
                  <X size={20} className="text-gray-400" />
                </button>
                <h2 className="text-2xl font-bold text-white mb-1">Checker Speed</h2>
              </div>

              <div className="p-6 space-y-3">
                {speeds.map((speed, index) => {
                  const Icon = speed.icon;
                  const isSelected = tempSpeed === speed.id;

                  return (
                    <motion.button
                      key={speed.id}
                      onClick={() => !speed.locked && setTempSpeed(speed.id)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? `${speed.borderColor} ${speed.bgColor}`
                          : `border-gray-700/50 bg-gray-800/30 ${!speed.locked ? 'hover:border-gray-600' : 'cursor-not-allowed'}`
                      }`}
                      disabled={speed.locked}
                      whileHover={!speed.locked ? { scale: 1.02 } : {}}
                      whileTap={!speed.locked ? { scale: 0.98 } : {}}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-start gap-3">
                        <motion.div
                          className={`w-10 h-10 rounded-lg ${speed.iconBg} flex items-center justify-center flex-shrink-0`}
                          animate={isSelected && !speed.locked ? { scale: [1, 1.1, 1] } : {}}
                          transition={{ duration: 0.3 }}
                        >
                          <Icon size={18} className={speed.textColor} />
                        </motion.div>
                        <div className="flex-1">
                          <h4 className={`font-bold text-base ${speed.locked ? 'text-gray-500' : 'text-white'}`}>
                            {speed.name}
                          </h4>
                          <p className={`text-xs ${speed.locked ? 'text-gray-600' : 'text-gray-400'} mb-1`}>
                            {speed.subtitle}
                          </p>
                          <p className={`text-xs ${speed.textColor}`}>
                            {speed.description}
                          </p>
                        </div>
                        <motion.div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${
                            isSelected && !speed.locked ? 'border-orange-500 bg-orange-500' : 'border-gray-600'
                          }`}
                        >
                          {isSelected && !speed.locked && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2 h-2 bg-white rounded-full"
                            />
                          )}
                        </motion.div>
                      </div>
                    </motion.button>
                  );
                })}

                <motion.button
                  onClick={() => setShowGuide(!showGuide)}
                  className="w-full mt-4 p-4 rounded-xl border border-purple-500/30 bg-purple-900/20 hover:bg-purple-900/30 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={20} className="text-purple-400" />
                      <span className="text-white font-semibold">Speed Control Guide</span>
                    </div>
                    <motion.div animate={{ rotate: showGuide ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <X size={20} className="text-purple-400" />
                    </motion.div>
                  </div>
                </motion.button>

                <AnimatePresence>
                  {showGuide && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 rounded-xl border border-purple-500/20 bg-purple-900/10 space-y-2">
                        {guideItems.map((item, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="flex items-start gap-2"
                          >
                            <CheckCircle2 size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-300 text-xs leading-relaxed">{item}</p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Proxy Toggle */}
                <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Power size={18} className="text-purple-400" />
                      <span className="text-white font-semibold">Use Proxy</span>
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setTempProxy(!tempProxy)}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        tempProxy ? 'bg-purple-500' : 'bg-gray-600'
                      }`}
                    >
                      <motion.div
                        className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-lg"
                        animate={{ x: tempProxy ? 30 : 2 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    </motion.button>
                  </div>
                  <p className="text-gray-400 text-xs">
                    {tempProxy ? 'Using your configured proxy' : 'Direct connection (no proxy)'}
                  </p>
                </div>

                {/* Sound Toggle */}
                <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {tempSound ? <Volume2 size={18} className="text-purple-400" /> : <VolumeX size={18} className="text-gray-500" />}
                      <span className="text-white font-semibold">Hit Sounds</span>
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setTempSound(!tempSound)}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        tempSound ? 'bg-purple-500' : 'bg-gray-600'
                      }`}
                    >
                      <motion.div
                        className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-lg"
                        animate={{ x: tempSound ? 30 : 2 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    </motion.button>
                  </div>
                  <p className="text-gray-400 text-xs">
                    {tempSound ? 'Play sound on APPROVED/LIVE hits' : 'Sounds disabled'}
                  </p>
                </div>

                {/* Hit Sender Selector */}
                <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Send size={18} className="text-purple-400" />
                    <span className="text-white font-semibold">Hit Sender</span>
                  </div>
                  {loading || hitSender === null ? (
                    <div className="text-center py-2">
                      <p className="text-gray-400 text-sm">Loading...</p>
                    </div>
                  ) : (
                    <>
                      <select
                        value={hitSender}
                        onChange={(e) => updateHitSender(e.target.value)}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500/50"
                      >
                        <option value="both">Both</option>
                        <option value="approved">Approved Only</option>
                        <option value="live">Live Only</option>
                        <option value="off">Off</option>
                      </select>
                      <p className="text-gray-400 text-xs mt-2">
                        {hitSender === 'both' && 'Receive all APPROVED and LIVE hits'}
                        {hitSender === 'approved' && 'Only receive APPROVED (charged) hits'}
                        {hitSender === 'live' && 'Only receive LIVE (CVV match) hits'}
                        {hitSender === 'off' && 'No personal notifications (public announcements still sent)'}
                      </p>
                    </>
                  )}
                </div>

                {/* Save Button */}
                <div className="mt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      setSelectedSpeed(tempSpeed);
                      setProxyEnabled(tempProxy);
                      setSoundEnabled(tempSound);
                      localStorage.setItem('checkerSpeed', tempSpeed);
                      localStorage.setItem('proxyEnabled', tempProxy);
                      localStorage.setItem('soundEnabled', tempSound);
                      
                      // Save hit_sender to database
                      await updateHitSender(hitSender);
                      
                      onClose();
                    }}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold py-3 rounded-xl"
                  >
                    Save Settings
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CheckerSpeedModal;
